<script>
import {defineComponent} from 'vue'
import OperationsModal from "./User/OperationsModal.vue";
import {axios} from "../mixins";

export default defineComponent({
  name: "MassUpdater",

  mixins: [
    axios
  ],

  components: {
    OperationsModal,
  },

  props: {
    indicatorOptions: {
      type: Object,
      required: true,
    },
    farmId: {
      type: Number,
      required: true,
    },
    farmIsLocked: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultType: {
      type: Number,
      required: false,
      default: null,
    },
    defaultYear: {
      type: Number,
      required: false
    },
    fullPage: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      year: null,
      updateType: null,
      fields: [],
      operationTypes: {
        1: 'harvests'
      },
      status: null,
      showStatus: false,
      instructions: '',
      defaultInstructions: 'Select a year and an operation type above to get started.',
      displayType: 'OperationsModal',
      selectOptions: null,
    }
  },

  created() {
    if (this.fullPage) {
      this.displayType = 'div';
    }

    // If loaded via blade, the string needs to be parsed
    if (typeof this.indicatorOptions === 'string') {
      this.selectOptions = JSON.parse(this.indicatorOptions)
    } else {
      this.selectOptions = this.indicatorOptions
    }

    this.year = this.defaultYear
    this.updateType = this.defaultType

    if (this.updateType) {
      this.$nextTick(function() {
        this.$refs.toolModal.open()
      })
    } else {
      this.updateType = 1
    }

    if (this.fullPage) {
      this.getData()
    }
  },

  computed: {
    typeSelections() {
      return {
        1: 'Actual Yields',
        2: 'Crop Type Confirmation'
      }
    }
  },

  methods: {
    getData() {
      this.clearStatus()
      this.fields = null;

      if (this.updateType) {
        this.get('/operationsUpdater/' + this.farmId + '?type=' + this.updateType + '&year=' + this.year).then((response) => {
          this.fields = response.data.fields
          this.instructions = response.data.instructions
        })
      } else {
        this.instructions = this.defaultInstructions
      }
    },

    saveData() {
      this.clearStatus()

      this.post('/operationsUpdater?type=' + this.updateType, this.fields).then((response) => {
        this.status = response.data.valid;
        this.showStatus = true;
      }).catch(() => {
        this.status = false
        this.showStatus = true;
      })
    },

    clearStatus() {
      this.showStatus = false;
      this.status = null;
    }
  }
})
</script>

<template>
  <Component :is="this.displayType" v-on:open="getData" ref="toolModal">
    <div class="w-full flex">
      <div class="rounded-t bg-secondary tab-shadow px-4 py-2 text-lg font-semibold block z-20">
        Operations
      </div>
    </div>

    <div class="bg-secondary tab-shadow w-full p-2 round-tr">
      <div class="flex flex-row gap-1">
        <select name="updateType"
                id="updateType"
                v-model="this.updateType"
                @change="this.getData"
                class="bg-primary italic border border-grey-light px-3 py-1 rounded leading-tight focus:outline-none">
          <option :value="null">Update Type</option>
          <template v-for="(label, type) in this.typeSelections">
            <option :value="Number.parseInt(type)">{{ label }}</option>
          </template>
        </select>

        <div v-if="this.updateType === 1">
          <select name="year"
                  id="year"
                  v-model="this.year"
                  @change="this.getData"
                  class="bg-primary italic border border-grey-light px-2 py-1 rounded leading-tight focus:outline-none">
            <option :value="null">Year</option>
            <template v-for="(year) in [2020, 2021, 2022, 2023, 2024, 2025, 2026]">
              <option :value="year">{{ year }}</option>
            </template>
          </select>
        </div>
      </div>

      <div class="mt-2 flex flex-col">
        <div class="my-1 w-full">
          {{ this.instructions }}
        </div>
        <div class="max-h-[50vh] overflow-y-scroll">
          <template v-for="field in this.fields">
            <div class="border-t w-full border-grey last:border-b">
              <h1 class="font-bold text-lg">
                {{ field.name }} <span class="font-normal">({{ field.size }} acres)</span>
              </h1>
              <div v-if="this.updateType === 1" class="ml-1">
                <template v-for="harvest in field.field_years[0].harvests">
                  <div class="flex flex-col lg:flex-row justify-between my-1">
                    <div>
                      <span class="mx-1 font-semibold">{{ this.selectOptions['crop_id'][harvest.crop_id].label }} Estimated Yield: </span>
                      <span class="mx-1">{{ Number.parseFloat(harvest.adjusted_yield ?? 0).toFixed(2) }} {{ harvest.adjusted_yield_units ? this.selectOptions['adjusted_yield_units'][harvest.adjusted_yield_units].label : '' }}</span>
                    </div>
                    <div>
                      <div class="flex flex-col lg:flex-row text-nowrap">
                        <div>
                          <label for="actual_yield" class="font-semibold mx-1">Actual Yield</label>
                          <input v-model="harvest.actual_yield"
                                 :required="harvest.actual_yield_units"
                                 name="actual_yield"
                                 class="bg-secondary italic rounded border border-grey-lighter w-24 px-1 leading-tight focus:outline-none focus:bg-secondary"
                                 type="number"
                                 min="0">
                        </div>

                        <div>
                          <label for="actual_yield_units" class="font-semibold mx-1">Actual Yield Units</label>
                          <select name="actual_yield_units"
                                  id="actual_yield_units"
                                  v-model="harvest.actual_yield_units"
                                  :required="harvest.actual_yield"
                                  class="cursor-pointer bg-secondary italic rounded border border-grey-lighter px-1 leading-tight focus:outline-none focus:ring">
                            <template v-for="option in this.selectOptions.adjusted_yield_units">
                              <option v-if="(option.local_active === true && option.is_active === true)
                                || (option.value === harvest.actual_yield_units)"
                                      :value="option.value">{{ option.label }}
                              </option>
                            </template>
                          </select>
                        </div>

                        <div v-if="harvest.number_of_cuttings">
                          <label for="actual_number_of_cuttings" class="font-semibold mx-1">Actual No. of Cuttings</label>
                          <input
                              v-model="harvest.actual_number_of_cuttings"
                              name="actual_number_of_cuttings"
                              class="bg-secondary italic rounded border border-grey-lighter w-24 px-1 leading-tight focus:outline-none focus:bg-secondary"
                              type="number"
                              min="0">
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
              <div v-if="this.updateType === 2" class="ml-1">
                <div class="flex flex-col lg:flex-row justify-between lg:justify-end my-1">
                  <div class="flex flex-col lg:flex-row text-nowrap">
                    <div>
                      <label for="primary_cash_crop" class="font-semibold mx-1">Primary Cash Crop</label>
                      <select v-model="field.field_years[0].primary_cash_crop"
                             :required="true"
                             name="primary_cash_crop"
                             class="bg-secondary italic rounded border border-grey-lighter px-1 leading-tight focus:outline-none focus:bg-secondary">
                        <option :value="null">
                          None
                        </option>
                        <template v-for="option in this.selectOptions.crop_id">
                          <option v-if="(option.local_active)"
                                  :value="option.value">
                            {{ option.label }}
                          </option>
                        </template>
                      </select>
                    </div>

                    <div>
                      <label for="secondary_cash_crop" class="font-semibold mx-1">Secondary Cash Crop</label>
                      <select v-model="field.field_years[0].secondary_cash_crop"
                              :required="true"
                              name="secondary_cash_crop"
                              class="bg-secondary italic rounded border border-grey-lighter px-1 leading-tight focus:outline-none focus:bg-secondary">
                        <option :value="null">
                          None
                        </option>
                        <template v-for="option in this.selectOptions.crop_id">
                          <option v-if="(option.local_active)"
                                  :value="option.value">
                            {{ option.label }}
                          </option>
                        </template>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <template #toggle="{open}">
      <button class="button bg-primary border-blue text-blue"
              @click="open"
              type="button"
              name="fileModal">
        <span>
          <i class="fas fa-list-check"/> Update Information
        </span>
      </button>
    </template>

    <template #footer="{close}" v-if="this.displayType === 'OperationsModal'">
      <button type="button"
              class="button button-wide mx-1 bg-tertiary text-sm hover:bg-grey-lighter focus:bg-grey-light"
              v-on:click="close">
        Close
      </button>

      <button class="button button-wide mx-1 text-white text-sm font-bold bg-green hover:bg-green-dark focus:bg-green-darker"
              v-on:click="saveData()">
        Save
      </button>

      <div v-show="this.showStatus"
           :class="'px-6 py-1 flex justify-center mx-1 font-bold '
           + (this.status ? 'text-green' : 'text-red')">
        {{ this.status ? 'Success!' : 'Error! Please review your data.' }}
      </div>
    </template>

    <div v-if="this.displayType === 'div'" class="bg-secondary footer-shadow rounded-b px-2 pb-3 pt-1 sm:flex sm:flex-row-reverse">
      <button class="button button-wide mx-1 text-white text-sm font-bold bg-green hover:bg-green-dark focus:bg-green-darker"
              v-on:click="saveData()">
        Save
      </button>

      <div v-show="this.showStatus"
           :class="'px-6 py-1 flex justify-center mx-1 font-bold '
           + (this.status ? 'text-green' : 'text-red')">
        {{ this.status ? 'Success!' : 'Error! Please review your data.' }}
      </div>
    </div>
  </Component>
</template>
